import type { ContentCardProps } from '../../types';
import { Card, Icon, Stack } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { getIconColor } from '~/utils';
import { CardFooter } from '../CardFooter';
import { buildCardMetadata } from '../../utils';
import { CardDescription, CardTitle } from '../CardText';

export interface PathCardProps extends ContentCardProps {
  progress?: number;
  total?: number | null;
  duration?: number | null;
  onStart?: () => void;
  onPreview?: () => void;
}

const { DEFAULT_COVER_IMAGES } = window.APP_CONSTANTS;

export const PathCard = ({
  name,
  imageSrc,
  description,
  progress,
  total,
  duration,
  assignmentStatus,
  onPreview,
  onStart,
  tags = [],
  skills = [],
  overflowMenuItems = {},
  hideContentCTA = false,
  actions,
}: PathCardProps) => {
  const theme = useTheme();
  const percentComplete = progress && total ? Math.round((progress / total) * 100) : 0;

  const GET_STARTED = I18n.t('js.learner.libraries.get_started');
  const PROGRESS = I18n.t('js.learner.libraries.path_progress', {
    progress: percentComplete,
  });
  const TRAININGS = I18n.t('js.learner.libraries.num_trainings', {
    numTrainings: total,
  });

  const metadata = buildCardMetadata(duration, skills, tags, theme.colors.on[600]);
  if (total) {
    metadata.push({
      iconName: 'collection:regular',
      text: TRAININGS,
      textColor: theme.colors.on[600],
      iconColor: theme.colors.on[600],
    });
  }

  const iconColor = getIconColor(theme.colors.decorative.green[500]);

  return (
    <Card onPress={onStart}>
      {imageSrc && (
        <Card.Hero
          src={imageSrc}
          errorSrc={DEFAULT_COVER_IMAGES.SERIES}
          icon={<Icon color={iconColor} name="path:regular" size="lg" />}
        />
      )}
      <Card.Container data-testid="library-card">
        <Card.Content>
          <Card.Body>
            <Stack size={4}>
              {!imageSrc && <Card.Icon iconColor={iconColor} iconName="path:regular" />}
              <Stack size={2}>
                <CardTitle text={name} />
                {metadata.length ? <Card.Metadata metadata={metadata} /> : null}
                <CardDescription text={description} color={theme.colors.on[500]} />
              </Stack>
            </Stack>
          </Card.Body>
        </Card.Content>
      </Card.Container>
      <CardFooter
        assignmentStatus={assignmentStatus}
        inProgressMessage={PROGRESS}
        startContentMessage={GET_STARTED}
        onStart={onStart}
        onPreview={onPreview}
        overflowMenuItems={overflowMenuItems}
        hideContentCTA={hideContentCTA}
        actions={actions}
      />
    </Card>
  );
};
